import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

// 3-field micro lead-capture. Saves to consultations collection.
// Designed for above-the-fold and inline sections — fast, low friction.
export default function QuickQuote({ variant = "panel", className = "" }) {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Name, phone, and a quick description please.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consultations", {
        full_name: form.name,
        // Backend requires email; we synthesize a placeholder from phone for the
        // quick-quote path so customers don't need to type one.
        email: `${form.phone.replace(/\D/g, "")}@quote.ozends.com`,
        phone: form.phone,
        message: form.message,
      });
      setDone(true);
      toast.success("Got it. We'll call you back today.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quick-quote-success"
        className={`border-2 border-yellow-500 bg-yellow-500/10 p-6 sm:p-8 text-stone-50 ${className}`}
      >
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-yellow-400" />
          <span className="label-eyebrow text-yellow-400">Request Received</span>
        </div>
        <h3 className="mt-3 font-display text-2xl sm:text-3xl font-bold">
          Thanks, {form.name.split(" ")[0]}. We'll call you back today.
        </h3>
        <p className="mt-2 text-stone-300">
          Want to talk sooner? Call or text{" "}
          <a href="tel:7152146563" className="text-yellow-400 underline">
            (715) 214-6563
          </a>
          .
        </p>
      </div>
    );
  }

  const isCompact = variant === "compact";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quick-quote-form"
      className={`border-2 border-yellow-500/40 bg-stone-900 ${
        isCompact ? "p-6" : "p-7 sm:p-9"
      } ${className}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
        <span className="label-eyebrow text-yellow-400">Free Estimate · 30-Second Form</span>
      </div>
      <h3
        className={`font-display font-bold text-stone-50 tracking-tight ${
          isCompact ? "text-2xl" : "text-3xl sm:text-4xl"
        }`}
      >
        Get a callback today.
      </h3>
      <p className="mt-2 text-stone-400 text-sm">
        We'll call or text you back same day with a free estimate. No pressure, no spam.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-300">
            Your name
          </Label>
          <Input
            data-testid="qq-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-300">
            Phone
          </Label>
          <Input
            data-testid="qq-phone"
            type="tel"
            inputMode="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none h-11"
          />
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <Label className="text-xs uppercase tracking-[0.2em] font-bold text-stone-300">
          What's the job?
        </Label>
        <Textarea
          data-testid="qq-message"
          rows={3}
          placeholder="e.g. Storm took down a tree in my backyard, need it cleared this week."
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="bg-stone-950 border-stone-700 focus:border-yellow-500 rounded-none"
        />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        data-testid="qq-submit"
        className="mt-5 w-full bg-yellow-500 text-yellow-950 hover:bg-yellow-400 font-bold uppercase tracking-wider rounded-none h-12 border-2 border-yellow-500 disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Get My Free Estimate"}
        <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </form>
  );
}
